import { useState, useEffect, useMemo } from 'react';
import {
  Newspaper, ExternalLink, RefreshCw, Star, Loader2, AlertTriangle, Clock,
  Filter, X,
} from 'lucide-react';
import { getNewsFeed } from '../services/api';
import { useActivePolling } from '../hooks/useActivePolling';

const FILTERS = [
  { id: 'hot',       label: 'Hot' },
  { id: 'rising',    label: 'Rising (3h)' },
  { id: 'important', label: 'Important' },
];

const QUICK_CURRENCIES = ['BTC', 'ETH', 'SOL', 'BNB'];
const MORE_CURRENCIES  = ['XRP', 'ADA', 'DOGE', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ARB', 'OP', 'AAVE'];

// Source brand colors — used for the source pill on each card.
const SOURCE_STYLES = {
  coindesk:        { text: 'text-blue-300',    bg: 'bg-blue-500/15',    border: 'border-blue-500/30' },
  cointelegraph:   { text: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30' },
  decrypt:         { text: 'text-pink-300',    bg: 'bg-pink-500/15',    border: 'border-pink-500/30' },
  theblock:        { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30' },
  bitcoinmagazine: { text: 'text-orange-300',  bg: 'bg-orange-500/15',  border: 'border-orange-500/30' },
};
const DEFAULT_SOURCE_STYLE = { text: 'text-gray-300', bg: 'bg-dark-700', border: 'border-dark-600' };

function timeAgo(ts) {
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function NewsPage() {
  const [filter, setFilter] = useState('hot');
  const [currency, setCurrency] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getNewsFeed({ filter, currencies: currency, kind: 'news' });
      setData(res?.data || res);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [filter, currency]);

  const interval = useActivePolling(5 * 60 * 1000, true);
  useEffect(() => {
    if (!interval) return;
    const id = setInterval(load, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, filter, currency]);

  // Open the more-currencies popover if user has a custom currency selected that's not in QUICK.
  useEffect(() => {
    if (currency && !QUICK_CURRENCIES.includes(currency) && currency !== '') setShowMore(true);
  }, [currency]);

  const heroPost = data?.posts?.[0];
  const restPosts = data?.posts?.slice(1) || [];

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <Newspaper className="w-7 h-7 text-cyan-400" />
          <h1 className="text-2xl font-bold text-white">Crypto News</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
            Multi-source RSS
          </span>
        </div>
        <p className="text-sm text-gray-400">
          Aggregated from CoinDesk, CoinTelegraph, Decrypt, The Block, and Bitcoin Magazine.
          <span className="text-emerald-400 ml-1">Real RSS feeds, cached 5 min.</span>
        </p>
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 items-center justify-between bg-dark-800/40 border border-dark-700/50 rounded-2xl p-3">
        <div className="flex gap-1.5 flex-wrap items-center">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                filter === f.id
                  ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-400'
                  : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-dark-600'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 items-center">
          <button onClick={() => setCurrency('')}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
              currency === ''
                ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-dark-600'
            }`}>All</button>
          {QUICK_CURRENCIES.map(c => (
            <button key={c} onClick={() => setCurrency(c)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                currency === c
                  ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                  : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-dark-600'
              }`}>{c}</button>
          ))}
          <div className="relative">
            <button
              onClick={() => setShowMore(s => !s)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all flex items-center gap-1 ${
                showMore || MORE_CURRENCIES.includes(currency)
                  ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                  : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-dark-600'
              }`}>
              <Filter className="w-3 h-3" />
              {MORE_CURRENCIES.includes(currency) ? currency : 'More'}
            </button>
            {showMore && (
              <div className="absolute right-0 mt-2 z-20 bg-dark-800 border border-dark-700 rounded-xl p-3 shadow-2xl w-64">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">More tokens</span>
                  <button onClick={() => setShowMore(false)} className="text-gray-500 hover:text-gray-300">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {MORE_CURRENCIES.map(c => (
                    <button
                      key={c}
                      onClick={() => { setCurrency(c); setShowMore(false); }}
                      className={`px-2 py-1.5 text-xs rounded-md border transition-all ${
                        currency === c
                          ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                          : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                      }`}>{c}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button onClick={load} disabled={loading}
            className="p-2 rounded-lg border border-dark-700 text-gray-400 hover:border-dark-600 disabled:opacity-50 ml-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-red-400 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {loading && !data ? (
        <SkeletonGrid />
      ) : !data?.posts?.length ? (
        <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
          <Newspaper className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No posts found for this filter.</p>
          {currency && (
            <p className="text-xs text-gray-600 mt-2">Try clearing the {currency} filter or switching to Hot.</p>
          )}
        </div>
      ) : (
        <>
          {heroPost && <HeroCard post={heroPost} />}
          {restPosts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {restPosts.map(p => <NewsCard key={p.id} post={p} />)}
            </div>
          )}
        </>
      )}

      {data && (
        <p className="text-[10px] text-gray-600 text-center">
          {data.posts?.length || 0} posts · {data.sourcesOk}/{data.sourcesTotal} sources OK ·{' '}
          {data.fromCache ? 'cached' : 'fresh'} · last fetched {timeAgo(data.fetchedAt)} ago
        </p>
      )}
    </div>
  );
}

// ─── Cards ──────────────────────────────────────────────────────────────────

function SourcePill({ source }) {
  const s = SOURCE_STYLES[source?.id] || DEFAULT_SOURCE_STYLE;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
      {source?.title || 'Source'}
    </span>
  );
}

function CurrencyChips({ currencies }) {
  if (!currencies?.length) return null;
  return (
    <div className="flex gap-1 flex-wrap">
      {currencies.slice(0, 6).map(c => (
        <span key={c.code} className="bg-dark-900 text-gray-300 px-1.5 py-0.5 rounded font-mono text-[9px] border border-dark-700">
          {c.code}
        </span>
      ))}
    </div>
  );
}

function HeroCard({ post }) {
  const positive = (post.votes?.positive || 0) + (post.votes?.liked || 0);
  const important = post.votes?.important || 0;

  return (
    <a href={post.url} target="_blank" rel="noopener noreferrer"
      className="group block bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl border border-dark-700/50 hover:border-cyan-500/30 overflow-hidden transition-all">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        <div className="md:col-span-1 bg-dark-900 aspect-video md:aspect-auto md:min-h-[200px] relative">
          {post.image ? (
            <img src={post.image} alt="" loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Newspaper className="w-12 h-12 text-dark-700" />
            </div>
          )}
          {important > 0 && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 backdrop-blur">
                <Star className="w-3 h-3" /> Important
              </span>
            </div>
          )}
        </div>
        <div className="md:col-span-2 p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <SourcePill source={post.source} />
            <span className="text-[10px] text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo(post.published_at)} ago
            </span>
            {post.author && <span className="text-[10px] text-gray-600">by {post.author}</span>}
            <CurrencyChips currencies={post.currencies} />
          </div>
          <h2 className="text-lg md:text-xl font-bold text-white leading-snug group-hover:text-cyan-300 transition-colors">
            {post.title}
          </h2>
          {post.excerpt && (
            <p className="text-sm text-gray-400 mt-2 leading-relaxed line-clamp-3">
              {post.excerpt}
            </p>
          )}
          <div className="mt-auto pt-3 flex items-center justify-between">
            <span className="text-xs text-cyan-400 group-hover:text-cyan-300 flex items-center gap-1">
              Read on {post.source?.title} <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

function NewsCard({ post }) {
  const important = post.votes?.important || 0;
  return (
    <a href={post.url} target="_blank" rel="noopener noreferrer"
      className="group bg-dark-800/60 hover:bg-dark-800 rounded-2xl border border-dark-700/50 hover:border-dark-600 overflow-hidden transition-all flex flex-col">
      {post.image && (
        <div className="aspect-[16/9] bg-dark-900 relative">
          <img src={post.image} alt="" loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
          {important > 0 && (
            <div className="absolute top-2 left-2">
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 backdrop-blur">
                <Star className="w-3 h-3" /> Important
              </span>
            </div>
          )}
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <SourcePill source={post.source} />
          <span className="text-[10px] text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {timeAgo(post.published_at)} ago
          </span>
          {!post.image && important > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">
              <Star className="w-3 h-3" /> Important
            </span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-white leading-snug group-hover:text-cyan-300 transition-colors line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-xs text-gray-400 mt-2 leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <CurrencyChips currencies={post.currencies} />
          <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-cyan-400" />
        </div>
      </div>
    </a>
  );
}

function SkeletonGrid() {
  return (
    <div className="space-y-4">
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 h-56 animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="bg-dark-800/40 rounded-2xl border border-dark-700/50 h-64 animate-pulse" />
        ))}
      </div>
    </div>
  );
}
